import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { getCardTypeInstruction, readModelNotes } from '@/ai/prompts/common/instructions'
import {
  diagramToJson,
  createUserMessage,
  createSystemMessage,
  getWorkflowBackgroundSections
} from '@/ai/utils/promptUtils'

export const buildSingleEventPrompt = ({
  parentEventId,
  workflow,
  svgGraph,
  svgLanes,
  cardTypes
}) => {
  const messages = []

  messages.push(createSystemMessage(systemMessages.EVENT_MODELING_DDD_EXPERT))

  const promptSections = []

  const { workflow: currentDiagram, currentStep } = workflow.eventsJson?.length
    ? diagramToJson(parentEventId, workflow, svgGraph, svgLanes)
    : { workflow: [], currentStep: undefined }
  const workflowBackgroundSections = getWorkflowBackgroundSections(workflow, currentDiagram, cardTypes)
  const currentStepObject = currentDiagram[currentStep - 1]
  const nextStepObject = currentDiagram[currentStep]

  let cardTypeInstructions = ''

  cardTypes.forEach((cardType) => {
    cardTypeInstructions += getCardTypeInstruction(cardType, 'Also add one')
  })

  const commandCardType = diagramHelpers.methods.getCommandCardType(cardTypes)
  const queryCardType = diagramHelpers.methods.getQueryCardType(cardTypes)

  promptSections.push(...workflowBackgroundSections)

  promptSections.push({
    title: 'Request',
    content: `
      Suggest one more step, after step ${currentStep}${currentDiagram[currentStep] ? ` and before step ${currentStep + 1}` : ''},
      that is relevant to the business process, workflow or project scope. The step must reflect what is
      typically happening in the process (a command invoked by an actor on an aggregate), after step
      ${currentStep} (${currentStepObject.role} - ${currentStepObject.description})
      ${currentDiagram[currentStep] ? ` and before step ${currentStep + 1} (${nextStepObject.role} - ${nextStepObject.description})` : ''}.
      The step must contain the role, actor or system name (if automated) with the max length of 18 characters.
      The step must contain the description of the event with the max length of 40 characters,
      the description should contain a verb in past tense and a noun.
    `,
    spacingAfter: (commandCardType || queryCardType || cardTypeInstructions) ? 1 : 2
  })

  if (commandCardType) {
    promptSections.push({
      content: `
        Also create a set of input fields. You are designing a simple input form (or mockup or wireframe)
        with data fields to be filled in and submitted by the actor (or role), in order to invoke a command
        on an aggregate, for this step of the process. Each data field is an input field that the user
        (or system, if automated) can fill in. The form reflects the information requested from the actor
        in order to invoke the command on the aggregate. The number of input fields per step can vary, but it
        is usually between 4 and 8. A data field name must be at most 30 characters long.
      `,
      spacingAfter: (queryCardType || cardTypeInstructions) ? 1 : 2
    })
  }

  if (queryCardType) {
    promptSections.push({
      content: `
        Also generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary
        decisions to take action and complete the step. The purpose of the Read Model (or Query Model) is to define
        a query (for one or many items) that displays the current system state in a way that is relevant to the actor
        BEFORE doing the task at hand. Provide the Read Model as a set of data fields to be displayed to the actor
        BEFORE invoking the current step command the aggregate root. Usually, between 2 and 8 fields are sufficient,
        but it can be more or less. Tag each data field with INDEXED if it should be indexed for filtering or search.
        An attribute can be a primitive or a reference to a non-primitive type like an associated entity.
        An attribute or field name must be at most 30 characters long.
      `,
      spacingAfter: 1
    })

    promptSections.push({
      title: 'Notes on Read Model',
      contentType: 'bullet',
      content: readModelNotes,
      spacingAfter: cardTypeInstructions ? 1 : 2
    })
  }

  if (cardTypeInstructions) {
    promptSections.push({ content: cardTypeInstructions })
  }

  const exampleData = {
    role: 'Role X',
    description: 'Approved invoice',
    commandExample: examples.commandExampleCreateOrder,
    readModelExample: examples.readModelExampleOrder
  }

  const eventExample = {
    event: getEventExample(exampleData, cardTypes, currentStep)
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('event', JSON.stringify(eventExample))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
